const CHUNK_ERROR_PATTERNS = [
  'Failed to fetch dynamically imported module',
  'Loading chunk',
  'Loading CSS chunk',
] as const;

const SESSION_KEY = '__st_chunk_reload';

/**
 * Detect whether an error message indicates a chunk/module load failure.
 * These occur when the browser requests a hashed asset that no longer
 * exists after a deployment.
 */
export function isChunkLoadError(
  message: string | undefined,
): boolean {
  if (!message) return false;
  return CHUNK_ERROR_PATTERNS.some((pattern) =>
    message.includes(pattern),
  );
}

/**
 * Install global error handlers that auto-reload the page when a
 * dynamic import fails due to a missing chunk. Guards against reload
 * loops by recording the reload in sessionStorage.
 *
 * Returns a cleanup function that removes the listeners.
 */
export function installChunkErrorHandler(): () => void {
  function handleError(event: ErrorEvent): void {
    if (isChunkLoadError(event.message)) {
      triggerReload();
    }
  }

  function handleRejection(event: PromiseRejectionEvent): void {
    const reason: unknown = event.reason;
    const message =
      reason instanceof Error ? reason.message : String(reason);
    if (isChunkLoadError(message)) {
      triggerReload();
    }
  }

  function triggerReload(): void {
    // Prevent infinite reload loops — only reload once per session
    if (sessionStorage.getItem(SESSION_KEY)) {
      return;
    }
    sessionStorage.setItem(SESSION_KEY, Date.now().toString());
    window.location.reload();
  }

  window.addEventListener('error', handleError);
  window.addEventListener('unhandledrejection', handleRejection);

  return () => {
    window.removeEventListener('error', handleError);
    window.removeEventListener(
      'unhandledrejection',
      handleRejection,
    );
  };
}
